import { useEffect, useRef, useState } from "react";

/**
 * Hero media origin. Defaults to the R2 bucket so the files are served from
 * one place we control (and can rate-limit / restrict hotlinking on) rather
 * than from the Pages deploy. Override with VITE_MEDIA_BASE; set it to an
 * empty string to fall back to the copies bundled in /public.
 */
const MEDIA_BASE = (
  import.meta.env.VITE_MEDIA_BASE ?? "https://assets.wintersix.net"
).replace(/\/$/, "");

const SOURCES = {
  webm: `${MEDIA_BASE}/videos/canvas.webm`,
  mp4: `${MEDIA_BASE}/videos/canvas.mp4`,
  poster: `${MEDIA_BASE}/images/canvas-poster.jpg`,
};

/**
 * Full-bleed hero video. The source is letterboxed against its own near-black
 * (#0d0908) backdrop, so the stage colour is matched to the footage and the
 * seam between video and page disappears.
 */
function HeroVideo() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (reduceMotion) {
      // Hold on the poster frame rather than looping.
      video.removeAttribute("autoplay");
      video.pause();
      setReady(true);
      return;
    }

    const play = () => {
      const attempt = video.play();
      if (attempt) attempt.catch(() => setReady(true));
    };

    play();

    // `loop` alone can drop out: a stalled buffer, a backgrounded tab, or an
    // iOS interruption can leave the element paused or parked on the last
    // frame. Re-arm on each of those instead of trusting the attribute.
    const onEnded = () => {
      video.currentTime = 0;
      play();
    };

    const onPause = () => {
      if (!video.ended && !document.hidden) play();
    };

    const onVisibility = () => {
      if (!document.hidden && video.paused) play();
    };

    video.addEventListener("ended", onEnded);
    video.addEventListener("pause", onPause);
    video.addEventListener("stalled", play);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      video.removeEventListener("ended", onEnded);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("stalled", play);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return (
    <div className="hero-video-stage">
      <video
        ref={videoRef}
        className={`hero-video ${ready ? "is-ready" : ""}`}
        poster={SOURCES.poster}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        disablePictureInPicture
        onCanPlay={() => setReady(true)}
        aria-label="Wintersix visual"
      >
        <source src={SOURCES.webm} type="video/webm" />
        <source src={SOURCES.mp4} type="video/mp4" />
      </video>
    </div>
  );
}

export default HeroVideo;
